export const FONT_DISPLAY =
  '"brieflands-display", "brieflands-regular", "IRANSansNe", "Vazir", Georgia, "Times New Roman", serif';

export const FONT_TEXT =
  '"brieflands-regular", "IRANSansNe", "Vazir", Georgia, "Times New Roman", serif';

export const FONT_UI =
  '"IRANSansNe", "Vazir", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const FONT_MONO =
  '"IRANSansNe", "Vazir", ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

export const SHEET_GUTTER = { xs: 1, sm: 1.5, lg: 2, xl: 3 } as const;

export const SHEET_MAX_WIDTH = 1400;

export const HEADING_SIZE = {
  page: { xs: "1.375rem", md: "1.5rem" },
  section: { xs: "1.1875rem", md: "1.25rem" },
  subsection: { xs: "1.0625rem", md: "1.125rem" },
  minor: "1rem",
} as const;

const SPINE_VARS = [
  "var(--bf-spine-teal)",
  "var(--bf-spine-green)",
  "var(--bf-spine-gold)",
  "var(--bf-spine-rust)",
  "var(--bf-spine-indigo)",
] as const;

export type SpineToken = (typeof SPINE_VARS)[number];

const CATEGORY_FAMILY: Record<string, SpineToken> = {
  "internal medicine": "var(--bf-spine-teal)",
  surgery: "var(--bf-spine-teal)",
  "clinical genetics": "var(--bf-spine-teal)",
  "radiological research": "var(--bf-spine-teal)",
  "morphology, cell biology, pathology": "var(--bf-spine-green)",
  physiology: "var(--bf-spine-green)",
  microbiology: "var(--bf-spine-green)",
  "microbiology, immunology, infectious diseases": "var(--bf-spine-green)",
  "pharmacological research": "var(--bf-spine-green)",
  "social medicine": "var(--bf-spine-gold)",
  "infectious diseases": "var(--bf-spine-rust)",
  psychiatry: "var(--bf-spine-indigo)",
};

const hashToSpine = (value: string): SpineToken => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return SPINE_VARS[Math.abs(hash) % SPINE_VARS.length];
};

export const spineForCategory = (category?: string | null): SpineToken => {
  const key = (category ?? "").trim().toLowerCase();
  if (!key) return "var(--bf-spine-teal)";
  return CATEGORY_FAMILY[key] ?? hashToSpine(key);
};

export const spineForJournal = (journal?: {
  categories?: string[] | null;
}): SpineToken => spineForCategory(journal?.categories?.[0]);

export const hairline = {
  borderBottom: "1px solid",
  borderColor: "custom.ruleHairline",
} as const;

export const hairlineTop = {
  borderTop: "1px solid",
  borderColor: "custom.ruleHairline",
} as const;

export const dottedRule = {
  borderBottom: "1px dotted",
  borderColor: "custom.ruleHairline",
} as const;

export const doubleRule = {
  borderBottom: "3px double",
  borderColor: "custom.ruleMasthead",
} as const;

export type ActionVariant = "solid" | "outline" | "ink" | "kicker";

export const actionSx = (
  variant: ActionVariant = "solid",
  size: "small" | "medium" = "medium",
) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.375rem",
  fontFamily: FONT_UI,
  fontWeight: 600,
  fontSize: size === "small" ? "0.75rem" : "0.8125rem",
  lineHeight: 1.4,
  letterSpacing: "0.01em",
  textAlign: "center" as const,
  textDecoration: "none",
  textTransform: "none" as const,
  borderRadius: 0,
  boxShadow: "none",
  "&:hover, &:active, &.Mui-focusVisible": { boxShadow: "none" },
  minWidth: 0,
  px: size === "small" ? 1.125 : 1.625,
  py: size === "small" ? 0.4375 : 0.6875,
  border: "1px solid",
  transition:
    "background-color .2s ease, border-color .2s ease, color .2s ease",
  ...(variant === "solid" && {
    backgroundColor: "primary.main",
    borderColor: "primary.main",
    color: "custom.inverted",
    "&:hover": {
      backgroundColor: "custom.linkHover",
      borderColor: "custom.linkHover",
      color: "custom.inverted",
    },
  }),
  ...(variant === "ink" && {
    backgroundColor: "custom.ink",
    borderColor: "custom.ink",
    color: "custom.newsprint",
    "&:hover": {
      backgroundColor: "custom.inkMuted",
      borderColor: "custom.inkMuted",
      color: "custom.newsprint",
    },
  }),
  ...(variant === "kicker" && {
    backgroundColor: "custom.kicker",
    borderColor: "custom.kicker",
    color: "custom.newsprint",
    "&:hover": {
      backgroundColor: "custom.ink",
      borderColor: "custom.ink",
      color: "custom.newsprint",
    },
  }),
  ...(variant === "outline" && {
    backgroundColor: "transparent",
    borderColor: "custom.link",
    color: "custom.link",
    "&:hover": {
      backgroundColor: "custom.newsprintRaised",
      borderColor: "custom.linkHover",
      color: "custom.linkHover",
    },
  }),
});

export const microActionSx = (tone = "custom.link") => ({
  display: "inline-flex",
  alignItems: "center",
  fontFamily: FONT_UI,
  fontSize: "0.5625rem",
  fontWeight: 700,
  lineHeight: 1.35,
  letterSpacing: "0.06em",
  color: tone,
  textDecoration: "none",
  border: "1px solid",
  borderColor: tone,
  borderRadius: 0,
  px: 0.25,
  py: 0.125,
  transition:
    "background-color .2s ease, border-color .2s ease, color .2s ease",
  "&:hover": {
    backgroundColor: "custom.newsprintRaised",
    borderColor: "custom.linkHover",
    color: "custom.linkHover",
  },
});

export const RAIL = "13rem";
export const RAIL_LEFT = RAIL;
export const RAIL_RIGHT = RAIL;

export const caretDownSx = (open = false) => ({
  flex: "none",
  width: 0,
  height: 0,
  borderStyle: "solid",
  borderWidth: "0.3125rem 0.28125rem 0 0.28125rem",
  borderColor: "currentColor transparent transparent transparent",
  transition: "transform .15s",
  transform: open ? "rotate(180deg)" : "none",
});

export const caretRightSx = (open = false) => ({
  flex: "none",
  width: 0,
  height: 0,
  borderStyle: "solid",
  borderWidth: "0.3125rem 0 0.3125rem 0.375rem",
  borderColor: "transparent transparent transparent currentColor",
  transformOrigin: "30% 50%",
  transition: "transform .15s",
  transform: open ? "rotate(90deg)" : "none",
});

export const caretLeftSx = {
  flex: "none",
  width: 0,
  height: 0,
  borderStyle: "solid",
  borderWidth: "0.3125rem 0.375rem 0.3125rem 0",
  borderColor: "transparent currentColor transparent transparent",
};

export const caretRightEndSx = {
  flex: "none",
  width: 0,
  height: 0,
  borderStyle: "solid",
  borderWidth: "0.3125rem 0 0.3125rem 0.375rem",
  borderColor: "transparent transparent transparent currentColor",
};

export const PINNED_HEADER = "3rem";
export const STICKY_TOP = "3.5rem";
export const STICKY_MAX_HEIGHT = "calc(100vh - 4.5rem)";

export const stickyRailSx = {
  position: "sticky" as const,
  top: STICKY_TOP,
  alignSelf: "start" as const,
  maxHeight: STICKY_MAX_HEIGHT,
  overflowY: "auto" as const,
  overscrollBehavior: "contain" as const,
};
